import {
  isNumberOperatorsType,
  isSetOperatorsType,
  type NumberOperatorsType,
  type SetOperatorsType,
} from '@/defs/common'
import type { LabelType, PartialExcept } from '@/defs/misc'
import { type ColorType } from '@/defs/mtg'

import { type IconType } from './icon'

/* Card Filter: Boolean */
export const CARD_FILTER_BOOLEAN_SLUGS = ['universesBeyond'] as const
export type CardFilterBooleanSlugType = (typeof CARD_FILTER_BOOLEAN_SLUGS)[number]
export const isCardFilterBooleanSlugType = (slug?: string): slug is CardFilterBooleanSlugType =>
  CARD_FILTER_BOOLEAN_SLUGS.includes(slug as CardFilterBooleanSlugType)

export type CardFilterBooleanType = {
  checked: boolean
  slug: CardFilterBooleanSlugType
}
export type CardFilterBooleanPartialType = PartialExcept<CardFilterBooleanType, 'slug'>
export const isCardFilterBooleanPartialType = (
  filter?: CardFilterPartialType,
): filter is CardFilterBooleanPartialType => isCardFilterBooleanSlugType(filter?.slug)
export const isCardFilterBooleanType = (filter?: CardFilterPartialType): filter is CardFilterBooleanType =>
  isCardFilterBooleanPartialType(filter) && filter.checked !== undefined

/* Card Filter: Colors */
export const CARD_FILTER_COLORS_SLUGS = ['colors'] as const
export type CardFilterColorsSlugType = (typeof CARD_FILTER_COLORS_SLUGS)[number]
export const isCardFilterColorsSlugType = (slug?: string): slug is CardFilterColorsSlugType =>
  CARD_FILTER_COLORS_SLUGS.includes(slug as CardFilterColorsSlugType)
export const CARD_FILTER_COLORS_OPERATORS = ['inclusiveAll', 'exclusiveAll', 'some', 'none'] as const
export type CardFilterColorsOperatorType = (typeof CARD_FILTER_COLORS_OPERATORS)[number]
export const isCardFilterColorsOperatorType = (operator?: string): operator is CardFilterColorsOperatorType =>
  CARD_FILTER_COLORS_OPERATORS.includes(operator as CardFilterColorsOperatorType)
export const CARD_FILTER_COLORS_OPERATOR_LABELS: LabelType<CardFilterColorsOperatorType> = {
  exclusiveAll: {
    label: 'Exactly these colors',
  },
  inclusiveAll: {
    label: 'Including these colors',
  },
  none: {
    label: 'None of these colors',
  },
  some: {
    label: 'At most these colors',
  },
}

export type CardFilterColorsType = {
  colors: ColorType[]
  operator: CardFilterColorsOperatorType
  slug: CardFilterColorsSlugType
}
export type CardFilterColorsPartialType = PartialExcept<CardFilterColorsType, 'slug'>
export const isCardFilterColorsPartialType = (filter?: CardFilterPartialType): filter is CardFilterColorsPartialType =>
  isCardFilterColorsSlugType(filter?.slug)
export const isCardFilterColorsType = (filter?: CardFilterPartialType): filter is CardFilterColorsType =>
  isCardFilterColorsPartialType(filter) &&
  isCardFilterColorsOperatorType(filter.operator) &&
  !!filter.colors &&
  filter.colors.length > 0

/* Card Filter: Search */
export const CARD_FILTER_SEARCH_SLUGS = ['cardName', 'typeLine'] as const
export type CardFilterSearchSlugType = (typeof CARD_FILTER_SEARCH_SLUGS)[number]
export const isCardFilterSearchSlugType = (slug?: string): slug is CardFilterSearchSlugType =>
  CARD_FILTER_SEARCH_SLUGS.includes(slug as CardFilterSearchSlugType)

export type CardFilterSearchType = {
  search: string
  slug: CardFilterSearchSlugType
}
export type CardFilterSearchPartialType = PartialExcept<CardFilterSearchType, 'slug'>
export const isCardFilterSearchPartialType = (filter?: CardFilterPartialType): filter is CardFilterSearchPartialType =>
  isCardFilterSearchSlugType(filter?.slug)
export const isCardFilterSearchType = (filter?: CardFilterPartialType): filter is CardFilterSearchType =>
  isCardFilterSearchPartialType(filter) && !!filter.search

/* Card Filter: Number */
export const CARD_FILTER_NUMBER_SLUGS = [
  'cmc',
  'power',
  'toughness',
  'decks',
  'inclusion',
  'lift',
  'synergy',
  'price',
  'salt',
] as const
export type CardFilterNumberSlugType = (typeof CARD_FILTER_NUMBER_SLUGS)[number]
export const isCardFilterNumberSlugType = (slug?: string): slug is CardFilterNumberSlugType =>
  CARD_FILTER_NUMBER_SLUGS.includes(slug as CardFilterNumberSlugType)

export type CardFilterNumberType = {
  number: number
  operator: NumberOperatorsType
  slug: CardFilterNumberSlugType
}
export type CardFilterNumberPartialType = PartialExcept<CardFilterNumberType, 'slug'>
export const isCardFilterNumberPartialType = (filter?: CardFilterPartialType): filter is CardFilterNumberPartialType =>
  isCardFilterNumberSlugType(filter?.slug)
export const isCardFilterNumberType = (filter?: CardFilterPartialType): filter is CardFilterNumberType =>
  isCardFilterNumberPartialType(filter) &&
  filter.number !== undefined &&
  !isNaN(filter.number) &&
  filter.number >= 0 &&
  isNumberOperatorsType(filter.operator)

/* Card Filter: Select */
export const CARD_FILTER_SELECT_SLUGS = ['rarity', 'tags', 'sets'] as const
export type CardFilterSelectSlugType = (typeof CARD_FILTER_SELECT_SLUGS)[number]
export const isCardFilterSelectSlugType = (slug?: string): slug is CardFilterSelectSlugType =>
  CARD_FILTER_SELECT_SLUGS.includes(slug as CardFilterSelectSlugType)

export type CardFilterSelectType = {
  label: string
  operator: SetOperatorsType
  selection: string
  slug: CardFilterSelectSlugType
}
export type CardFilterSelectPartialType = PartialExcept<CardFilterSelectType, 'slug'>
export const isCardFilterSelectPartialType = (filter?: CardFilterPartialType): filter is CardFilterSelectPartialType =>
  isCardFilterSelectSlugType(filter?.slug)
export const isCardFilterSelectType = (filter?: CardFilterPartialType): filter is CardFilterSelectType =>
  isCardFilterSelectPartialType(filter) && !!filter.label && isSetOperatorsType(filter.operator) && !!filter.selection

/* Card Filter: All */
export type CardFilterSlugType =
  | CardFilterBooleanSlugType
  | CardFilterColorsSlugType
  | CardFilterNumberSlugType
  | CardFilterSearchSlugType
  | CardFilterSelectSlugType
export const CARD_FILTER_SLUGS: CardFilterSlugType[] = [
  'cardName',
  'typeLine',
  'colors',
  'cmc',
  'power',
  'toughness',
  'rarity',
  'universesBeyond',
  'sets',
  'tags',
  'price',
  'salt',
  'inclusion',
  'decks',
  'lift',
  'synergy',
] as const
export const CARD_FILTER_LABELS: LabelType<CardFilterSlugType, { icon: IconType; labelLong?: string }> = {
  cardName: { icon: 'cards', label: 'Card Name' },
  cmc: { icon: 'calculator', label: 'Mana Value' },
  colors: { icon: 'mtg-wubrg', label: 'Colors' },
  decks: { icon: 'hashtag', label: 'Deck #' },
  inclusion: { icon: 'percent', label: 'Inclusion %' },
  lift: { icon: 'anglesUp', label: 'Lift' },
  power: { icon: 'sword', label: 'Power' },
  price: { icon: 'dollarSign', label: 'Price' },
  rarity: { icon: 'gem', label: 'Rarity' },
  salt: { icon: 'salt', label: 'Salt' },
  sets: { icon: 'layerGroup', label: 'Set' },
  synergy: { icon: 'wandMagicSparkles', label: 'Synergy' },
  tags: { icon: 'tag', label: 'Tag' },
  toughness: { icon: 'shield', label: 'Toughness' },
  typeLine: { icon: 'tableCells', label: 'Type Line' },
  universesBeyond: {
    icon: 'crystalBall',
    label: 'Universes Beyond',
    labelLong: 'Hide Universes Beyond Exclusive Cards',
  },
}
export type CardFilterType =
  CardFilterBooleanType | CardFilterColorsType | CardFilterNumberType | CardFilterSearchType | CardFilterSelectType
export type CardFilterPartialType =
  | CardFilterSearchPartialType
  | CardFilterNumberPartialType
  | CardFilterBooleanPartialType
  | CardFilterColorsPartialType
  | CardFilterSelectPartialType
export const isCardFilterType = (filter?: CardFilterPartialType): filter is CardFilterType =>
  isCardFilterBooleanType(filter) ||
  isCardFilterColorsType(filter) ||
  isCardFilterNumberType(filter) ||
  isCardFilterSearchType(filter) ||
  isCardFilterSelectType(filter)

export const CARD_FILTERS_SLUGS_CARD_REQUIRED: CardFilterSlugType[] = [
  'cardName',
  'typeLine',
  'colors',
  'cmc',
  'power',
  'toughness',
  'rarity',
  'universesBeyond',
  'sets',
  'tags',
  'price',
  'salt',
]

export const withCardFilterDefaults = (
  slug: CardFilterSlugType,
  filter?: CardFilterPartialType,
): CardFilterPartialType => {
  if (isCardFilterBooleanSlugType(slug)) {
    return { checked: true, ...(isCardFilterBooleanPartialType(filter) ? filter : undefined), slug }
  } else if (isCardFilterColorsSlugType(slug)) {
    return {
      colors: [],
      operator: 'inclusiveAll',
      ...(isCardFilterColorsPartialType(filter) ? filter : undefined),
      slug,
    }
  } else if (isCardFilterNumberSlugType(slug)) {
    return { operator: 'atLeast', ...(isCardFilterNumberPartialType(filter) ? filter : undefined), slug }
  } else if (isCardFilterSearchSlugType(slug)) {
    return { search: '', ...(isCardFilterSearchPartialType(filter) ? filter : undefined), slug }
  }
  return { label: '', operator: 'include', ...(isCardFilterSelectPartialType(filter) ? filter : undefined), slug }
}
